import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Label from '../../../../../Label';
import ImageUpload from '../../../../../ImageUpload';
import useAssetImageUpload from '../../../../../../../hooks/useAssetImageUpload';
import { IMAGE_SIZE_CODE } from '../../../../../../../constants/image-size-codes';
import { PRINT_ASSET_TYPES } from '../../../../../../../constants/print-assets';
import RangeSlider from '../../../../../RangeSlider';
import Input from '../../../../../Input';
import { PHOTO_TYPES } from '../../../../../../../constants/photo-types';
import useOpenModal from '../../../../../../../hooks/useOpenModal';
import PrintBackgroundFormModal, {
  PRINT_ASSET_FORM_MODAL,
} from '../../../../../../../apps/pro/views/AlbumFolder/AlbumFolders/AlbumFolderPictureDay/PrintAssetForm';
import CardDesignerAssetPicker from '../CardDesignerAssetPicker';

const propTypes = {
  className: PropTypes.string,
  // Without an element, selecting/uploading an asset creates the foreground
  // element on the fly.
  element: PropTypes.shape({
    cardElement: PropTypes.object.isRequired,
    cardElementConfiguration: PropTypes.object.isRequired,
  }),
  onCardElementCreate: PropTypes.func.isRequired,
  onElementConfigurationChange: PropTypes.func.isRequired,
  zElementIndexMax: PropTypes.number.isRequired,
  orientation: PropTypes.string,
};

const CardDesignerForegroundControls = ({
  className,
  element,
  onCardElementCreate,
  onElementConfigurationChange,
  zElementIndexMax,
  orientation,
}) => {
  const [opacity, setOpacity] = useState(1);

  const openModal = useOpenModal();

  const { cardElementConfiguration } = element ?? {};
  const { opacity: elementOpacity } = cardElementConfiguration ?? {};

  useEffect(() => {
    setOpacity(elementOpacity ?? 1);
  }, [elementOpacity]);

  const classes = classNames({
    [className]: !!className,
  });

  const applyForegroundConfiguration = elementConfig => {
    if (element) {
      onElementConfigurationChange({
        element,
        elementConfig,
      });
    } else {
      onCardElementCreate({
        cardElementType: 'PHOTO',
        cardElementConfiguration: {
          is_editable: false,
          ...elementConfig,
          photo_type: PHOTO_TYPES.FOREGROUND,
        },
        input: {
          frame: {
            x: 0,
            y: 0,
            width: 1,
            height: 1,
          },
          z_index: zElementIndexMax,
        },
      });
    }
  };

  const { uploading, onUpload, onUploadReset } = useAssetImageUpload({
    onImageUploaded: async assetImage => {
      applyForegroundConfiguration({
        asset_image_id: assetImage.id,
      });
    },
  });

  return (
    <div className={classes}>
      <Label
        id="foreground-control"
        labelTip="Foregrounds are used for graphic elements only. Please upload images containing athletes or students to a photo box and use the Layering feature to adjust to your preferred placement."
      >
        Foreground Assets
      </Label>
      {cardElementConfiguration?.asset_image && (
        <div className="card-designer-foreground-opacity-container">
          <span className="mr-1">Opacity</span>
          <RangeSlider
            className="mx-2"
            onChange={values => {
              setOpacity(Math.round((values[0] / 1e2) * 1e2) / 1e2);
            }}
            onFinalChange={values => {
              onElementConfigurationChange({
                element,
                elementConfig: {
                  opacity: Math.round((values[0] / 1e2) * 1e2) / 1e2,
                },
              });
            }}
            values={[Math.round(opacity * 1e2)]}
            min={0}
            max={100}
          />
          <Input
            id="opacity"
            type="number"
            max={100}
            min={0}
            className="mr-1"
            value={Math.round(opacity * 1e2)}
            onChange={({ target }) => {
              const percentValue = Math.round((target.value / 1e2) * 1e2) / 1e2;
              setOpacity(percentValue);
              onElementConfigurationChange({
                element,
                elementConfig: {
                  opacity: percentValue,
                },
              });
            }}
          />{' '}
          <span className="input-addon-text">%</span>
        </div>
      )}
      <CardDesignerAssetPicker
        printAssetType={PRINT_ASSET_TYPES.FOREGROUND}
        searchPlaceholder="Search your foregrounds.."
        orientation={orientation}
        currentAssetImageId={cardElementConfiguration?.asset_image?.id}
        onSaveAsAsset={() =>
          openModal(PRINT_ASSET_FORM_MODAL, {
            printAssetType: PRINT_ASSET_TYPES.FOREGROUND,
            defaultAssetImage: cardElementConfiguration.asset_image,
          })
        }
        onAssetSelect={printAsset =>
          applyForegroundConfiguration({
            existing_asset_image_id: printAsset.asset_image.id,
          })
        }
        uploadControl={
          <ImageUpload
            id="card-designer-foreground-image"
            buttonText="Upload an image"
            loading={uploading}
            disabled={uploading}
            customFileInputAccept="image/x-png, image/png, .png"
            image={
              cardElementConfiguration?.asset_image
                ? {
                    id: cardElementConfiguration.asset_image.id,
                    thumbnail_urls: Object.values(IMAGE_SIZE_CODE).map(
                      size => ({
                        size_code: size,
                        url: cardElementConfiguration.asset_image.url,
                        width: 0,
                        height: 0,
                      }),
                    ),
                  }
                : null
            }
            onInputChange={event => {
              const [file] = event.target.files;
              onUpload(file);
            }}
            onImageDismiss={() => {
              onUploadReset();
              onElementConfigurationChange({
                element,
                elementConfig: {
                  asset_image_id: null,
                },
              });
            }}
          />
        }
      />
      <PrintBackgroundFormModal />
    </div>
  );
};

CardDesignerForegroundControls.propTypes = propTypes;

export default CardDesignerForegroundControls;
